import type {
  AppearanceBanner,
  AppearanceComponent,
  AppearanceComponentContent,
  AppearanceRow,
} from '@setplex/tria-api'
import type { Effect } from 'effector'
import type { Row } from './index.h'

import { api } from '@setplex/tria-api'
import { createEffect, createEvent, createStore, sample } from 'effector'
import { isType } from 'variant'
import { profilesModel } from '~/entities/profiles'
import { appearanceFeature } from '~/features/appearance'
import { CONTENT } from '~/features/appearance/components'
import { aye, nay } from '~/shared/helpers'
import { logger } from '~/shared/logger'
import { filterBannersByIdentifier } from '../banners'
import {
  Awaiting,
  Dirty,
  Empty,
  Failed,
  Loading,
  Ready,
  Updating,
} from './index.h'
import { toDirty, toLoading } from './lib'
import {
  $loadQueue,
  $structure,
  leaveModule,
  resetStructure,
  setStructure,
} from './model'

const $identifier = appearanceFeature.$currentIdentifier

export const resetRowsContent = createEvent()

export const dirty = createEvent()

const $refreshedProfileByIdentifier = createStore<
  Record<string, number | null>
>({}).reset(resetStructure)

const isPendingContentState = (data: Row['data']) =>
  isType(data, Loading) || isType(data, Updating)

const resetInterruptedContent = (comp: Row): Row => {
  if (isType(comp.data, Loading)) {
    return {
      ...comp,
      data: Empty(),
    }
  }

  if (isType(comp.data, Updating)) {
    return {
      ...comp,
      data: Dirty(comp.data.payload),
    }
  }

  return comp
}

// content-request
export const getComponentContentFx: Effect<
  {
    appearance: AppearanceComponent
    identifier: string | null
    profileId: number | null
  },
  AppearanceComponentContent
> = createEffect(async (component) => {
  if (component.appearance.contentType === CONTENT.BANNER_CAROUSEL) {
    const bannersContent = await api.appearance.getBannerContentFx(
      component.appearance as AppearanceBanner
    )

    return filterBannersByIdentifier(component.identifier, bannersContent)
  }

  return api.appearance.getRowContentFx(component.appearance as AppearanceRow)
})

sample({
  clock: leaveModule,
  target: dirty,
})

sample({
  clock: setStructure,
  source: {
    refreshedProfileByIdentifier: $refreshedProfileByIdentifier,
    profileId: profilesModel.$activeProfileId,
  },
  filter: (
    { refreshedProfileByIdentifier, profileId },
    { identifier, structure }
  ) => aye(structure) && refreshedProfileByIdentifier[identifier] !== profileId,
  fn: (_, { structure }) => {
    const updated = new Map()

    for (const [id, comp] of structure!) updated.set(id, toDirty(comp))

    return updated
  },
  target: $structure,
})

sample({
  clock: setStructure,
  source: {
    refreshedProfileByIdentifier: $refreshedProfileByIdentifier,
    profileId: profilesModel.$activeProfileId,
    queue: $loadQueue,
  },
  filter: (
    { refreshedProfileByIdentifier, profileId },
    { identifier, structure }
  ) => aye(structure) && refreshedProfileByIdentifier[identifier] !== profileId,
  fn: ({ queue }, { identifier, structure }) => {
    const queuedKeys = new Set(
      queue.map(({ identifier, component }) => `${identifier}:${component.id}`)
    )
    const updatedQueue = [...queue]

    for (const [, { desc }] of structure!) {
      const queueKey = `${identifier}:${desc.id}`

      if (desc.refreshable && !queuedKeys.has(queueKey)) {
        queuedKeys.add(queueKey)
        updatedQueue.push({ identifier: identifier!, component: desc })
      }
    }

    return updatedQueue
  },
  target: $loadQueue,
})

sample({
  clock: setStructure,
  source: {
    refreshedProfileByIdentifier: $refreshedProfileByIdentifier,
    profileId: profilesModel.$activeProfileId,
  },
  filter: (_, { structure }) => aye(structure),
  fn: ({ refreshedProfileByIdentifier, profileId }, { identifier }) => ({
    ...refreshedProfileByIdentifier,
    [identifier]: profileId,
  }),
  target: $refreshedProfileByIdentifier,
})

// mark empty rows as loading, and dirty rows as updating
sample({
  clock: getComponentContentFx,
  source: $structure,
  filter: (structure) => aye(structure),
  fn: (structure, { appearance: { id } }) => {
    if (!structure) return structure

    const comp = structure.get(id)
    if (!comp) return structure

    const newComp = new Map(structure)
    return newComp.set(id, toLoading(comp))
  },
  target: $structure,
  batch: false,
})

// get loaded comp data and update comp in the list
// skip updating if comp's state was changed from loading|updating (for example user left the page)
sample({
  clock: getComponentContentFx.done,
  source: {
    structure: $structure,
    identifier: $identifier,
    profileId: profilesModel.$activeProfileId,
  },
  filter: ({ structure, identifier, profileId }, { params }) => {
    const { id } = params.appearance

    if (!identifier || identifier !== params.identifier || !structure)
      return false

    const comp = structure!.get(id)
    if (!comp) return false

    if (profileId !== params.profileId && comp.desc.refreshable) return false

    const data = comp.data
    return (
      isType(data, Loading) ||
      isType(data, Updating) ||
      isType(data, Empty) ||
      isType(data, Dirty)
    )
  },
  fn: ({ structure }, { params, result }) => {
    const { content, last } = result
    const { id } = params.appearance
    const comp = structure!.get(id)

    if (!comp) return structure
    const { desc } = comp

    const updated = new Map(structure!)

    // Update to Awaiting
    updated.set(id, {
      desc,
      data: Awaiting(content),
      hasMore: !last,
    })

    // Awaiting -> Ready for all rows
    for (const [key, value] of updated) {
      const { data, hasMore, desc } = value
      if (isType(data, Awaiting)) {
        updated.set(key, {
          desc,
          data: Ready(data.payload),
          hasMore,
        })
      }
    }

    return updated
  },
  target: $structure,
})

// update failed comp content in the list
// skip updating if comp's state was changed from loading|updating (for example user left the page)
sample({
  clock: getComponentContentFx.fail,
  source: {
    structure: $structure,
    identifier: $identifier,
    profileId: profilesModel.$activeProfileId,
  },
  filter: ({ structure, identifier, profileId }, { params }) => {
    const { id } = params.appearance

    if (!identifier || identifier !== params.identifier || nay(structure))
      return false

    const comp = structure!.get(id)
    if (!comp) return false

    if (profileId !== params.profileId && comp.desc.refreshable) return false

    const data = comp.data
    return (
      isType(data, Loading) ||
      isType(data, Updating) ||
      isType(data, Empty) ||
      isType(data, Dirty)
    )
  },
  fn: ({ structure }, { params, error }) => {
    const { id } = params.appearance

    const comp = structure!.get(id)

    if (!comp) return structure

    const { desc, hasMore } = comp

    return new Map(structure).set(id, {
      desc,
      hasMore,
      data: Failed(error),
    })
  },
  target: $structure,
})

sample({
  clock: getComponentContentFx.finally,
  source: {
    structure: $structure,
    identifier: $identifier,
    profileId: profilesModel.$activeProfileId,
  },
  filter: ({ structure, identifier, profileId }, { params }) => {
    const { id } = params.appearance

    if (
      !identifier ||
      identifier !== params.identifier ||
      profileId === params.profileId ||
      nay(structure)
    )
      return false

    const comp = structure!.get(id)

    return (
      Boolean(comp?.desc.refreshable) &&
      Boolean(comp?.data && isPendingContentState(comp.data))
    )
  },
  fn: ({ structure }, { params }) => {
    const { id } = params.appearance
    const comp = structure!.get(id)

    if (!comp) return structure

    return new Map(structure).set(id, resetInterruptedContent(comp))
  },
  target: $structure,
})

sample({
  clock: getComponentContentFx.finally,
  source: {
    structure: $structure,
    identifier: $identifier,
    profileId: profilesModel.$activeProfileId,
    queue: $loadQueue,
  },
  filter: ({ structure, identifier, profileId, queue }, { params }) => {
    const { id } = params.appearance

    if (
      !identifier ||
      identifier !== params.identifier ||
      profileId === params.profileId ||
      nay(structure) ||
      queue.some(
        ({ identifier, component }) =>
          identifier === params.identifier && component.id === id
      )
    )
      return false

    const comp = structure!.get(id)

    return (
      Boolean(comp?.desc.refreshable) &&
      Boolean(comp?.data && isPendingContentState(comp.data))
    )
  },
  fn: ({ structure, queue }, { params }) => {
    const comp = structure!.get(params.appearance.id)

    return comp
      ? [...queue, { identifier: params.identifier!, component: comp.desc }]
      : queue
  },
  target: $loadQueue,
})

// mark refreshable rows as dirty (or reset them to empty, if they are not ready)
sample({
  clock: dirty,
  source: $structure,
  filter: (structure) => aye(structure),
  fn: (structure) => {
    const updated = new Map()

    for (const [id, comp] of structure!) updated.set(id, toDirty(comp))

    return updated
  },
  target: $structure,
})

sample({
  clock: getComponentContentFx.fail,
  fn: ({ params, error }) => ['failed to load component', params, ':', error],
  target: logger.errorFx,
})

const MAX_PARALLEL_LOAD = 3

sample({
  clock: getComponentContentFx,
  source: $loadQueue,
  fn: (queue, { appearance: { id }, identifier }) =>
    queue.some(
      (item) => item.identifier === identifier && item.component.id === id
    )
      ? queue.filter(
          (item) => item.identifier !== identifier || item.component.id !== id
        )
      : queue,
  target: $loadQueue,
})

// watch load queue and start loading rows if there are free slots for parallel loading
sample({
  clock: [
    $loadQueue,
    getComponentContentFx.inFlight,
    $identifier,
    profilesModel.$activeProfileId,
  ],
  source: {
    queue: $loadQueue,
    inFlight: getComponentContentFx.inFlight,
    identifier: $identifier,
    profileId: profilesModel.$activeProfileId,
  },
  filter: ({ queue, inFlight, identifier }) =>
    queue.some((item) => item.identifier === identifier) &&
    inFlight < MAX_PARALLEL_LOAD &&
    Boolean(identifier),
  fn: ({ queue, identifier, profileId }) => {
    const item = queue.find((item) => item.identifier === identifier)

    return {
      appearance: item!.component,
      identifier: item!.identifier,
      profileId,
    }
  },
  target: getComponentContentFx,
})
